import { useState, useCallback } from 'react';
import { Banknote, Sparkles, AlertCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useCard } from '@/contexts/CardContext';
import { decodeCashuToken, formatAmount, shortenMintUrl, isCashuToken } from '@/lib/cashu';
import { generateQrDataUrl } from '@/lib/qrcode';

export function Step1Token() {
  const { card, updateCard, setStep } = useCard();
  const [tokenInput, setTokenInput] = useState(card.cashuToken);
  const [error, setError] = useState('');
  const [isDecoded, setIsDecoded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleTokenChange = useCallback((value: string) => {
    setTokenInput(value);
    setError('');
    setIsDecoded(false);

    const trimmed = value.trim();
    if (!trimmed) return;

    if (!isCashuToken(trimmed)) {
      setError('This doesn\'t look like a Cashu token. Tokens start with "cashuA" or "cashuB".');
      return;
    }

    const result = decodeCashuToken(trimmed);
    if (!result) {
      setError('Could not decode this Cashu token. Please check it and try again.');
      return;
    }

    updateCard({
      cashuToken: result.encoded,
      amount: result.amount,
      mint: result.mint,
      unit: result.unit,
    });
    setIsDecoded(true);
  }, [updateCard]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData('text');
    // Let the onChange handler deal with the pasted text
    setTimeout(() => handleTokenChange(pasted), 0);
  }, [handleTokenChange]);

  const handleContinue = useCallback(async () => {
    if (!isDecoded) return;
    setIsGenerating(true);
    try {
      const qrDataUrl = await generateQrDataUrl(card.cashuToken, {
        width: 600,
        color: { dark: card.theme.qrFg, light: card.theme.qrBg },
      });
      updateCard({ qrDataUrl });
      setStep(2);
    } catch {
      setError('Failed to generate QR code. Token may be too large.');
    } finally {
      setIsGenerating(false);
    }
  }, [isDecoded, card.cashuToken, card.theme, updateCard, setStep]);

  return (
    <div className="space-y-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
      {/* YouTube video */}
      <div className="max-w-xl mx-auto">
        <div className="relative w-full overflow-hidden rounded-2xl shadow-lg border border-border/50" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src="https://www.youtube.com/embed/XwQG2H-AutY"
            title="CashuCards - How It Works"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      </div>

      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/25">
          <Banknote className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight">Paste Your Cashu Token</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Paste a Cashu eCash token to embed into a beautiful greeting card. The token value will be displayed on the card with a scannable QR code.
        </p>
      </div>

      <div className="space-y-4 max-w-xl mx-auto">
        <div className="space-y-2">
          <Label htmlFor="cashu-token" className="text-sm font-medium">
            Cashu Token
          </Label>
          <Textarea
            id="cashu-token"
            placeholder="cashuB..."
            value={tokenInput}
            onChange={(e) => handleTokenChange(e.target.value)}
            onPaste={handlePaste}
            className="min-h-[120px] font-mono text-xs resize-none transition-all duration-200 focus:ring-2 focus:ring-amber-500/50"
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm animate-in fade-in-0 duration-300">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {isDecoded && (
          <div className="p-4 rounded-xl border-2 border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <Check className="w-5 h-5" />
              <span className="font-semibold">Token Decoded Successfully</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-0 text-sm px-3 py-1">
                <Sparkles className="w-3 h-3 mr-1" />
                {formatAmount(card.amount, card.unit)}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {shortenMintUrl(card.mint)}
              </Badge>
            </div>
          </div>
        )}

        <Button
          onClick={handleContinue}
          disabled={!isDecoded || isGenerating}
          size="lg"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-amber-500/30 disabled:opacity-50 disabled:shadow-none"
        >
          {isGenerating ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
              Generating QR...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              Continue to Design
              <Sparkles className="w-4 h-4" />
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}
